import { useState } from 'react'

// Source definitions from backend
interface Source {
  id: string
  name: string
  tier: 'national' | 'state' | 'lhn' | 'specialty'
  url?: string
  state?: string
  lhn?: string
  defaultOn: boolean
  description: string
}

interface UserLocation {
  state?: string
  lhn?: string
}

interface UserSourcePrefs {
  national: string[]
  state: string[]
  local: string[]
  specialty: string[]
}

interface SourceToggleProps {
  userPrefs: UserSourcePrefs
  userLocation?: UserLocation
  onPrefsChange: (prefs: UserSourcePrefs) => void
  isCollapsed?: boolean
}

// Mock source data based on backend sources.ts
const ALL_SOURCES: Source[] = [
  // National Tier
  { id: 'etg', name: 'eTG Complete', tier: 'national', defaultOn: true, description: 'Therapeutic Guidelines - Australian prescribing guidelines' },
  { id: 'racgp', name: 'RACGP Guidelines', tier: 'national', defaultOn: true, description: 'RACGP clinical guidelines including Red Book' },
  { id: 'pbs', name: 'PBS', tier: 'national', defaultOn: true, description: 'PBS listings, restrictions, and pricing' },
  { id: 'mbs', name: 'MBS', tier: 'national', defaultOn: true, description: 'MBS item numbers, billing requirements, and schedules' },
  { id: 'amh', name: 'AMH', tier: 'national', defaultOn: true, description: 'Evidence-based drug information and guidance' },
  { id: 'nps', name: 'NPS MedicineWise', tier: 'national', defaultOn: true, description: 'Independent medicines and health information' },
  { id: 'healthdirect', name: 'Healthdirect', tier: 'national', defaultOn: true, description: 'Patient information and handouts' },
  { id: 'pubmed', name: 'PubMed', tier: 'national', defaultOn: true, description: 'Medical literature database (NCBI)' },
  { id: 'aihw', name: 'AIHW', tier: 'national', defaultOn: true, description: 'Australian Institute of Health and Welfare data and reports' },
  { id: 'tga', name: 'TGA', tier: 'national', defaultOn: true, description: 'Therapeutic Goods Administration drug database' },

  // State Tier
  { id: 'sa_health', name: 'SA Health', tier: 'state', state: 'SA', defaultOn: true, description: 'South Australia Health clinical guidelines' },
  { id: 'nsw_health', name: 'NSW Health', tier: 'state', state: 'NSW', defaultOn: true, description: 'New South Wales Health guidelines' },
  { id: 'qld_health', name: 'Queensland Health', tier: 'state', state: 'QLD', defaultOn: true, description: 'Queensland Health clinical resources' },
  { id: 'vic_health', name: 'Better Health Channel', tier: 'state', state: 'VIC', defaultOn: true, description: 'Victoria Health information and guidelines' },
  { id: 'wa_health', name: 'WA Health', tier: 'state', state: 'WA', defaultOn: true, description: 'Western Australia Health Department guidelines' },
  { id: 'nt_health', name: 'NT Health', tier: 'state', state: 'NT', defaultOn: true, description: 'Northern Territory Health - remote and Indigenous focus' },
  { id: 'tas_health', name: 'Tasmania Health', tier: 'state', state: 'TAS', defaultOn: true, description: 'Tasmania Department of Health guidelines' },
  { id: 'act_health', name: 'ACT Health', tier: 'state', state: 'ACT', defaultOn: true, description: 'Australian Capital Territory Health Directorate' },

  // LHN Tier
  { id: 'healthpathways_adl', name: 'HealthPathways Adelaide', tier: 'lhn', state: 'SA', lhn: 'CALHN', defaultOn: false, description: 'Adelaide HealthPathways - local referral pathways' },
  { id: 'sa_calhn', name: 'CALHN', tier: 'lhn', state: 'SA', lhn: 'CALHN', defaultOn: false, description: 'Central Adelaide Local Health Network' },
  { id: 'sa_nalhn', name: 'NALHN', tier: 'lhn', state: 'SA', lhn: 'NALHN', defaultOn: false, description: 'Northern Adelaide Local Health Network' },
  { id: 'sa_salhn', name: 'SALHN', tier: 'lhn', state: 'SA', lhn: 'SALHN', defaultOn: false, description: 'Southern Adelaide Local Health Network' },

  // Specialty Tier
  { id: 'mja', name: 'Medical Journal of Australia', tier: 'specialty', defaultOn: false, description: 'Australian medical research and clinical articles' },
  { id: 'cochrane', name: 'Cochrane Library', tier: 'specialty', defaultOn: false, description: 'Systematic reviews and evidence-based medicine' },
  { id: 'cancer_aus', name: 'Cancer Australia', tier: 'specialty', defaultOn: false, description: 'National cancer control guidelines and resources' },
  { id: 'heartfoundation', name: 'Heart Foundation', tier: 'specialty', defaultOn: false, description: 'Cardiovascular disease prevention and management' },
  { id: 'diabetes_aus', name: 'Diabetes Australia', tier: 'specialty', defaultOn: false, description: 'Diabetes management and education resources' },
  { id: 'asthma_aus', name: 'National Asthma Council', tier: 'specialty', defaultOn: false, description: 'Asthma and respiratory disease guidelines' },
]

function SourceItem({ source, isEnabled, onToggle, canDisable = true }: {
  source: Source
  isEnabled: boolean
  onToggle: (enabled: boolean) => void
  canDisable?: boolean
}) {
  const [showTooltip, setShowTooltip] = useState(false)

  return (
    <div className="flex items-center justify-between py-2 px-1">
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-2">
          <span className="text-sm font-medium text-gray-700 truncate">
            {source.name}
          </span>
          <div className="relative">
            <button
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>

            {showTooltip && (
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg z-10">
                <div className="font-medium mb-1">{source.name}</div>
                <div>{source.description}</div>
                {source.url && (
                  <div className="mt-1 text-blue-300">{source.url}</div>
                )}
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
              </div>
            )}
          </div>
        </div>
      </div>

      <button
        onClick={() => onToggle(!isEnabled)}
        disabled={!canDisable && isEnabled}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          isEnabled ? 'bg-blue-600' : 'bg-gray-200'
        } ${!canDisable && isEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        <span
          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            isEnabled ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  )
}

function TierSection({
  title,
  sources,
  enabledIds,
  onSourceToggle,
  isExpanded,
  onToggleExpanded,
  showCount = true,
  minEnabled = 0
}: {
  title: string
  sources: Source[]
  enabledIds: string[]
  onSourceToggle: (sourceId: string, enabled: boolean) => void
  isExpanded: boolean
  onToggleExpanded: () => void
  showCount?: boolean
  minEnabled?: number
}) {
  const enabledCount = enabledIds.length

  return (
    <div className="mb-4">
      <button
        onClick={onToggleExpanded}
        className="w-full flex items-center justify-between p-2 text-left hover:bg-gray-50 rounded-md"
      >
        <div>
          <span className="text-sm font-semibold text-gray-800">{title}</span>
          {showCount && (
            <span className="ml-2 text-xs text-gray-500">
              {enabledCount}/{sources.length} enabled
            </span>
          )}
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="mt-2 ml-2 space-y-1">
          {sources.map((source) => {
            const isEnabled = enabledIds.includes(source.id)
            const canDisable = !(source.tier === 'national' && enabledCount <= minEnabled && isEnabled)

            return (
              <SourceItem
                key={source.id}
                source={source}
                isEnabled={isEnabled}
                onToggle={(enabled) => onSourceToggle(source.id, enabled)}
                canDisable={canDisable}
              />
            )
          })}
          {sources.length === 0 && (
            <p className="text-sm text-gray-500 py-2 px-1">
              No sources available for this tier
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default function SourceToggle({
  userPrefs,
  userLocation,
  onPrefsChange,
  isCollapsed = false
}: SourceToggleProps) {
  const [expandedSections, setExpandedSections] = useState({
    national: true,  // National expanded by default
    state: false,
    lhn: false,
    specialty: false
  })

  // Filter sources by user location
  const nationalSources = ALL_SOURCES.filter(s => s.tier === 'national')
  const stateSources = ALL_SOURCES.filter(s =>
    s.tier === 'state' && (!userLocation?.state || s.state === userLocation.state)
  )
  const lhnSources = ALL_SOURCES.filter(s =>
    s.tier === 'lhn' &&
    (!userLocation?.state || s.state === userLocation.state) &&
    (!userLocation?.lhn || s.lhn === userLocation.lhn)
  )
  const specialtySources = ALL_SOURCES.filter(s => s.tier === 'specialty')

  const handleSourceToggle = (tier: keyof UserSourcePrefs, sourceId: string, enabled: boolean) => {
    const currentIds = userPrefs[tier] || []
    let newIds: string[]

    if (enabled) {
      newIds = [...currentIds, sourceId]
    } else {
      newIds = currentIds.filter(id => id !== sourceId)
    }

    // Ensure at least one national source is enabled
    if (tier === 'national' && newIds.length === 0) {
      return // Don't allow disabling all national sources
    }

    const newPrefs = {
      ...userPrefs,
      [tier]: newIds
    }

    onPrefsChange(newPrefs)
  }

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  const totalEnabled =
    userPrefs.national.length +
    userPrefs.state.length +
    userPrefs.local.length +
    userPrefs.specialty.length

  if (isCollapsed) {
    return (
      <div className="text-center text-sm text-gray-600">
        <div className="font-medium">{totalEnabled}</div>
        <div className="text-xs">sources</div>
      </div>
    )
  }

  return (
    <div className="space-y-1">
      <TierSection
        title="National Sources"
        sources={nationalSources}
        enabledIds={userPrefs.national}
        onSourceToggle={(sourceId, enabled) => handleSourceToggle('national', sourceId, enabled)}
        isExpanded={expandedSections.national}
        onToggleExpanded={() => toggleSection('national')}
        minEnabled={1} // At least 1 national source required
      />

      <TierSection
        title="State Sources"
        sources={stateSources}
        enabledIds={userPrefs.state}
        onSourceToggle={(sourceId, enabled) => handleSourceToggle('state', sourceId, enabled)}
        isExpanded={expandedSections.state}
        onToggleExpanded={() => toggleSection('state')}
      />

      <TierSection
        title="Local Health Network"
        sources={lhnSources}
        enabledIds={userPrefs.local}
        onSourceToggle={(sourceId, enabled) => handleSourceToggle('local', sourceId, enabled)}
        isExpanded={expandedSections.lhn}
        onToggleExpanded={() => toggleSection('lhn')}
      />

      <TierSection
        title="Specialty Resources"
        sources={specialtySources}
        enabledIds={userPrefs.specialty}
        onSourceToggle={(sourceId, enabled) => handleSourceToggle('specialty', sourceId, enabled)}
        isExpanded={expandedSections.specialty}
        onToggleExpanded={() => toggleSection('specialty')}
      />

      {userLocation && (
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <div className="text-xs text-blue-700">
            <div className="font-medium mb-1">Your Location</div>
            <div>State: {userLocation.state || 'Not specified'}</div>
            {userLocation.lhn && <div>LHN: {userLocation.lhn}</div>}
          </div>
        </div>
      )}
    </div>
  )
}